// src/pages/admin/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

// Note: Nous devrons créer ces actions et le slice pour les paramètres
import { fetchSettings, updateSettings, addDepartureCity, deleteDepartureCity } from '../../features/settings/settingsSlice';

const SettingsPage = () => {
  const dispatch = useDispatch();
  // Nous utiliserons le state local en attendant de créer le slice settings
  const [settings, setSettings] = useState({
    siteName: 'Hajj Packages',
    contactEmail: 'contact@hajj-packages.com',
    contactPhone: '+33 1 23 45 67 89',
    departureCities: ['Paris', 'Lyon', 'Marseille', 'Lille', 'Toulouse', 'Nice', 'Bordeaux'],
    maxPackagesPerPage: 12,
    maxCompaniesPerPage: 10,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // État pour le formulaire d'ajout de ville
  const [newCity, setNewCity] = useState('');
  
  // État pour la notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Dans une version future, nous chargerons les paramètres depuis l'API
  // useEffect(() => {
  //   dispatch(fetchSettings());
  // }, [dispatch]);
  
  // Gestionnaire de changement pour les champs de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };
  
  // Gestionnaire pour l'ajout d'une nouvelle ville
  const handleAddCity = () => {
    if (newCity && !settings.departureCities.includes(newCity)) {
      setSettings((prev) => ({
        ...prev,
        departureCities: [...prev.departureCities, newCity],
      }));
      setNewCity('');
      
      // Dans une version future avec API:
      // dispatch(addDepartureCity(newCity));
    }
  };
  
  // Gestionnaire pour la suppression d'une ville
  const handleDeleteCity = (city) => {
    setSettings((prev) => ({
      ...prev,
      departureCities: prev.departureCities.filter((c) => c !== city),
    }));
    
    // Dans une version future avec API:
    // dispatch(deleteDepartureCity(city));
  };
  
  // Soumettre le formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simuler un chargement
    setIsLoading(true);
    
    // Simuler un appel API
    setTimeout(() => {
      setIsLoading(false);
      setNotification({
        open: true,
        message: 'Paramètres mis à jour avec succès',
        severity: 'success',
      });
      
      // Dans une version future avec API:
      // dispatch(updateSettings(settings));
    }, 800);
  };
  
  // Fermer la notification
  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Paramètres du site
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Paramètres généraux */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Paramètres généraux
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nom du site"
                    name="siteName"
                    value={settings.siteName}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email de contact"
                    name="contactEmail"
                    type="email"
                    value={settings.contactEmail}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Téléphone de contact"
                    name="contactPhone"
                    value={settings.contactPhone}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Forfaits par page"
                    name="maxPackagesPerPage"
                    type="number"
                    value={settings.maxPackagesPerPage}
                    onChange={handleChange}
                    required
                    inputProps={{ min: 1, max: 50 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Compagnies par page"
                    name="maxCompaniesPerPage"
                    type="number"
                    value={settings.maxCompaniesPerPage}
                    onChange={handleChange}
                    required
                    inputProps={{ min: 1, max: 50 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    sx={{ mt: 2 }}
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Enregistrer'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
        
        {/* Gestion des villes de départ */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Villes de départ
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Nouvelle ville"
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    placeholder="Ajouter une ville"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCity}
                    disabled={!newCity || settings.departureCities.includes(newCity)}
                    fullWidth
                    sx={{ height: '100%' }}
                  >
                    Ajouter
                  </Button>
                </Grid>
              </Grid>
            </Box>
            
            <List>
              {settings.departureCities.map((city) => (
                <ListItem key={city} divider>
                  <ListItemText primary={city} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteCity(city)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            
            {settings.departureCities.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                Aucune ville de départ définie
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;